/**
 * Saved Searches Component
 * Displays and manages user's saved property searches.
 * Card-grid layout with modal-based create/edit and Electric Coral polish.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useSavedSearches } from '../../agents/notificationAgent';
import './SavedSearchesComponent.css';
import { PROPERTY_TYPE_OPTIONS, propertyTypeLabel } from '../../utils/propertyKind';
import { findRangeErrors, hasRangeErrors } from '../../utils/rangeValidation';
import FieldError from '../ui/FieldError';

const EMPTY_CRITERIA_FORM = {
  city: '',
  state: '',
  zip_code: '',
  min_price: '',
  max_price: '',
  min_bedrooms: '',
  max_bedrooms: '',
  min_bathrooms: '',
  max_bathrooms: '',
  min_sqft: '',
  max_sqft: '',
  property_type: '',
  sort_by: '',
  sort_order: '',
};

const EMPTY_NOTIFICATIONS = {
  notifyNewListing: true,
  notifyPriceDrop: true,
  frequency: 'immediate',
};

/**
 * Strip empty, null, undefined values from an object.
 * Converts numeric string values to numbers for known numeric fields.
 */
function buildCleanCriteria(raw, searchType) {
  if (searchType === 'semantic') {
    return { search_type: 'semantic', query: raw.query || '' };
  }

  const numericFields = new Set([
    'min_price', 'max_price', 'min_bedrooms', 'max_bedrooms',
    'min_bathrooms', 'max_bathrooms', 'min_sqft', 'max_sqft',
  ]);

  const cleaned = { search_type: 'criteria' };
  for (const [key, val] of Object.entries(raw)) {
    if (val == null || val === '') continue;
    if (numericFields.has(key)) {
      const num = Number(val);
      if (!isNaN(num) && num > 0) cleaned[key] = num;
    } else {
      cleaned[key] = val;
    }
  }
  return cleaned;
}

/**
 * Populate form state from existing search criteria
 */
function criteriaToForm(criteria) {
  if (!criteria) return { ...EMPTY_CRITERIA_FORM };
  const form = { ...EMPTY_CRITERIA_FORM };
  for (const key of Object.keys(form)) {
    if (criteria[key] != null && criteria[key] !== '') {
      form[key] = String(criteria[key]);
    }
  }
  return form;
}

/**
 * Inline criteria fields grid — shared between create and edit forms
 */
function CriteriaFieldsGrid({ values, onChange, idPrefix, errors = {} }) {
  const field = (name, label, type = 'text', extra = {}) => (
    <div className="ss-field" key={name}>
      <label htmlFor={`${idPrefix}-${name}`}>{label}</label>
      <input
        id={`${idPrefix}-${name}`}
        type={type}
        name={name}
        value={values[name] || ''}
        onChange={onChange}
        placeholder={extra.placeholder || label}
        maxLength={extra.maxLength}
        min={extra.min}
        step={extra.step}
        aria-invalid={errors[name] ? true : undefined}
        aria-describedby={errors[name] ? `${idPrefix}-${name}-error` : undefined}
      />
      <FieldError id={`${idPrefix}-${name}-error`} message={errors[name]} />
    </div>
  );

  return (
    <div className="ss-criteria-grid">
      <div className="ss-field-group">
        <span className="ss-field-group-label">Location</span>
        <div className="ss-field-row">
          {field('city', 'City')}
          {field('state', 'State', 'text', { placeholder: 'e.g. TX', maxLength: 2 })}
          {field('zip_code', 'ZIP Code', 'text', { placeholder: '78701' })}
        </div>
      </div>
      <div className="ss-field-group">
        <span className="ss-field-group-label">Property Type</span>
        <div className="ss-field-row">
          <div className="ss-field">
            <label htmlFor={`${idPrefix}-property_type`}>Property type</label>
            <select
              id={`${idPrefix}-property_type`}
              name="property_type"
              value={values.property_type || ''}
              onChange={onChange}
            >
              <option value="">Any</option>
              {PROPERTY_TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
      <div className="ss-field-group">
        <span className="ss-field-group-label">Price Range</span>
        <div className="ss-field-row">
          {field('min_price', 'Min Price', 'number', { placeholder: '$0', min: 0 })}
          {field('max_price', 'Max Price', 'number', { placeholder: 'Any', min: 0 })}
        </div>
      </div>
      <div className="ss-field-group">
        <span className="ss-field-group-label">Features</span>
        <div className="ss-field-row">
          {field('min_bedrooms', 'Min Beds', 'number', { min: 0, step: 1 })}
          {field('max_bedrooms', 'Max Beds', 'number', { min: 0, step: 1 })}
          {field('min_bathrooms', 'Min Baths', 'number', { min: 0, step: 0.5 })}
          {field('max_bathrooms', 'Max Baths', 'number', { min: 0, step: 0.5 })}
        </div>
        <div className="ss-field-row">
          {field('min_sqft', 'Min sqft', 'number', { min: 0 })}
          {field('max_sqft', 'Max sqft', 'number', { min: 0 })}
        </div>
      </div>
      <div className="ss-field-group">
        <span className="ss-field-group-label">Sort</span>
        <div className="ss-field-row">
          <div className="ss-field">
            <label htmlFor={`${idPrefix}-sort_by`}>Sort By</label>
            <select
              id={`${idPrefix}-sort_by`}
              name="sort_by"
              value={values.sort_by || ''}
              onChange={onChange}
            >
              <option value="">Default (price)</option>
              <option value="price">Price</option>
              <option value="newest">Newest</option>
              <option value="address">Address</option>
            </select>
          </div>
          <div className="ss-field">
            <label htmlFor={`${idPrefix}-sort_order`}>Order</label>
            <select
              id={`${idPrefix}-sort_order`}
              name="sort_order"
              value={values.sort_order || ''}
              onChange={onChange}
            >
              <option value="">Default (asc)</option>
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Notification options row — shared between create and edit
 */
function NotificationOptions({ values, onChange, idPrefix = 'ss' }) {
  return (
    <div className="ss-notifications-row">
      <label className="ss-checkbox-label">
        <input
          type="checkbox"
          checked={values.notifyNewListing}
          onChange={(e) => onChange({ ...values, notifyNewListing: e.target.checked })}
        />
        <span>🔔 New Listings</span>
      </label>
      <label className="ss-checkbox-label">
        <input
          type="checkbox"
          checked={values.notifyPriceDrop}
          onChange={(e) => onChange({ ...values, notifyPriceDrop: e.target.checked })}
        />
        <span>💰 Price Drops</span>
      </label>
      <div className="ss-field ss-field--inline">
        <label htmlFor={`${idPrefix}-frequency`}>Frequency</label>
        <select
          id={`${idPrefix}-frequency`}
          value={values.frequency}
          onChange={(e) => onChange({ ...values, frequency: e.target.value })}
        >
          <option value="immediate">Immediate</option>
          <option value="daily">Daily</option>
          <option value="weekly">Weekly</option>
        </select>
      </div>
    </div>
  );
}

/**
 * Search type radio toggle — pill-style segmented control
 */
function SearchTypeToggle({ activeType, onSwitch }) {
  return (
    <div className="ss-mode-toggle" role="radiogroup" aria-label="Search type">
      <button
        type="button"
        role="radio"
        aria-checked={activeType === 'criteria'}
        className={`ss-mode-toggle__option ${activeType === 'criteria' ? 'ss-mode-toggle__option--active' : ''}`}
        onClick={() => onSwitch('criteria')}
      >
        🔍 Criteria
      </button>
      <button
        type="button"
        role="radio"
        aria-checked={activeType === 'semantic'}
        className={`ss-mode-toggle__option ${activeType === 'semantic' ? 'ss-mode-toggle__option--active' : ''}`}
        onClick={() => onSwitch('semantic')}
      >
        🧠 Semantic
      </button>
    </div>
  );
}

/**
 * Render concise criteria summary chips for a card
 */
function CriteriaTags({ criteria }) {
  if (!criteria) return null;
  const isSemantic = criteria.search_type === 'semantic';

  return (
    <div className="search-criteria-summary">
      <span className={`criteria-tag criteria-tag--type ${isSemantic ? 'criteria-tag--semantic' : 'criteria-tag--criteria'}`}>
        {isSemantic ? '🧠 Semantic' : '🔍 Criteria'}
      </span>

      {isSemantic ? (
        criteria.query && (
          <span className="criteria-tag criteria-tag--query" title={criteria.query}>
            "{criteria.query}"
          </span>
        )
      ) : (
        <>
          {criteria.city && <span className="criteria-tag">📍 {criteria.city}</span>}
          {criteria.state && <span className="criteria-tag">{criteria.state}</span>}
          {criteria.zip_code && <span className="criteria-tag">ZIP: {criteria.zip_code}</span>}
          {criteria.property_type && (
            <span className="criteria-tag">🏷️ {propertyTypeLabel(criteria.property_type)}</span>
          )}
          {(criteria.min_price || criteria.max_price) && (
            <span className="criteria-tag">
              💰 {criteria.min_price ? `$${Number(criteria.min_price).toLocaleString()}` : '$0'}
              {' – '}
              {criteria.max_price ? `$${Number(criteria.max_price).toLocaleString()}` : 'Any'}
            </span>
          )}
          {(criteria.min_bedrooms || criteria.max_bedrooms) && (
            <span className="criteria-tag">
              🛏️ {criteria.min_bedrooms || 0}–{criteria.max_bedrooms || 'Any'} Beds
            </span>
          )}
          {(criteria.min_bathrooms || criteria.max_bathrooms) && (
            <span className="criteria-tag">
              🚿 {criteria.min_bathrooms || 0}–{criteria.max_bathrooms || 'Any'} Baths
            </span>
          )}
          {(criteria.min_sqft || criteria.max_sqft) && (
            <span className="criteria-tag">
              📐 {criteria.min_sqft ? Number(criteria.min_sqft).toLocaleString() : '0'}–{criteria.max_sqft ? Number(criteria.max_sqft).toLocaleString() : 'Any'} sqft
            </span>
          )}
          {criteria.sort_by && criteria.sort_by !== 'price' && (
            <span className="criteria-tag">Sort: {criteria.sort_by}</span>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Modal shell for create/edit — backdrop, dialog, esc-to-close
 */
function SearchFormModal({ title, onClose, children }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div className="ss-modal-overlay" onClick={onClose}>
      <div
        className="ss-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ss-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="ss-modal-header">
          <h3 id="ss-modal-title">{title}</h3>
          <button
            type="button"
            className="ss-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </header>
        <div className="ss-modal-body">{children}</div>
      </div>
    </div>
  );
}

/**
 * Reusable form body for both create and edit
 */
function SearchForm({
  name, setName,
  searchType, setSearchType,
  criteria, onCriteriaChange, idPrefix,
  semanticQuery, setSemanticQuery,
  notifications, setNotifications,
  onSubmit, onCancel,
  submitLabel,
}) {
  // Inverted min/max ranges can't be saved (QA run 5 F19 / run 7 S3).
  const rangeErrors = searchType === 'criteria' ? findRangeErrors(criteria) : {};
  return (
    <form onSubmit={onSubmit} className="ss-form">
      <div className="ss-form-field">
        <label htmlFor={`${idPrefix}-name`}>Name</label>
        <input
          id={`${idPrefix}-name`}
          type="text"
          placeholder="Search name (e.g., 'Austin 3BR Under $500K')"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="search-input"
          autoFocus
        />
      </div>

      <SearchTypeToggle activeType={searchType} onSwitch={setSearchType} />

      {searchType === 'criteria' ? (
        <CriteriaFieldsGrid
          values={criteria}
          onChange={onCriteriaChange}
          idPrefix={idPrefix}
          errors={rangeErrors}
        />
      ) : (
        <div className="ss-semantic-input">
          <label htmlFor={`${idPrefix}-semantic-query`}>Describe what you're looking for</label>
          <textarea
            id={`${idPrefix}-semantic-query`}
            placeholder="e.g., Modern 3-bedroom home near downtown with a large backyard and updated kitchen"
            value={semanticQuery}
            onChange={(e) => setSemanticQuery(e.target.value)}
            rows={3}
          />
        </div>
      )}

      <div className="ss-form-section">
        <span className="ss-section-label">Notifications</span>
        <NotificationOptions
          values={notifications}
          onChange={setNotifications}
          idPrefix={idPrefix}
        />
      </div>

      <div className="form-buttons">
        <button type="button" onClick={onCancel} className="cancel-button">
          Cancel
        </button>
        <button type="submit" className="save-button" disabled={hasRangeErrors(rangeErrors)}>
          {submitLabel}
        </button>
      </div>
    </form>
  );
}

export default function SavedSearchesComponent({ userId, onSelectSearch }) {
  const {
    searches,
    isLoading,
    error,
    createSearch,
    updateSearch,
    deleteSearch,
    checkNewListings,
  } = useSavedSearches(userId);

  // Create form state
  const [isCreating, setIsCreating] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createSearchType, setCreateSearchType] = useState('criteria');
  const [createCriteria, setCreateCriteria] = useState({ ...EMPTY_CRITERIA_FORM });
  const [createSemanticQuery, setCreateSemanticQuery] = useState('');
  const [createNotifications, setCreateNotifications] = useState({ ...EMPTY_NOTIFICATIONS });

  // Edit state
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editSearchType, setEditSearchType] = useState('criteria');
  const [editCriteria, setEditCriteria] = useState({ ...EMPTY_CRITERIA_FORM });
  const [editSemanticQuery, setEditSemanticQuery] = useState('');
  const [editNotifications, setEditNotifications] = useState({ ...EMPTY_NOTIFICATIONS });

  const resetCreateForm = useCallback(() => {
    setCreateName('');
    setCreateSearchType('criteria');
    setCreateCriteria({ ...EMPTY_CRITERIA_FORM });
    setCreateSemanticQuery('');
    setCreateNotifications({ ...EMPTY_NOTIFICATIONS });
    setIsCreating(false);
  }, []);

  const handleCreateCriteriaChange = (e) => {
    const { name, value } = e.target;
    setCreateCriteria((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditCriteriaChange = (e) => {
    const { name, value } = e.target;
    setEditCriteria((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateSearch = async (e) => {
    e.preventDefault();

    if (!createName.trim()) {
      alert('Please enter a name for the saved search');
      return;
    }

    if (createSearchType === 'semantic' && !createSemanticQuery.trim()) {
      alert('Please enter a search query');
      return;
    }

    // The form shows the inline range errors; don't save until they're fixed.
    if (createSearchType === 'criteria' && hasRangeErrors(findRangeErrors(createCriteria))) {
      return;
    }

    try {
      const rawCriteria = createSearchType === 'semantic'
        ? { query: createSemanticQuery }
        : createCriteria;
      const criteria = buildCleanCriteria(rawCriteria, createSearchType);

      await createSearch(createName, criteria, {
        notifyNewListing: createNotifications.notifyNewListing,
        notifyPriceDrop: createNotifications.notifyPriceDrop,
        frequency: createNotifications.frequency,
      });

      resetCreateForm();
    } catch (err) {
      console.error('Failed to create search:', err);
    }
  };

  const handleStartEdit = (search) => {
    setEditingId(search.id);
    setEditName(search.name);

    const type = search.criteria?.search_type || 'criteria';
    setEditSearchType(type);

    if (type === 'semantic') {
      setEditSemanticQuery(search.criteria?.query || '');
      setEditCriteria({ ...EMPTY_CRITERIA_FORM });
    } else {
      setEditSemanticQuery('');
      setEditCriteria(criteriaToForm(search.criteria));
    }

    setEditNotifications({
      notifyNewListing: search.notify_on_new_listing ?? true,
      notifyPriceDrop: search.notify_on_price_drop ?? true,
      frequency: search.notify_frequency || 'immediate',
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditName('');
    setEditCriteria({ ...EMPTY_CRITERIA_FORM });
    setEditSemanticQuery('');
  };

  const handleSaveEdit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!editingId) return;

    if (!editName.trim()) {
      alert('Please enter a name for the saved search');
      return;
    }

    if (editSearchType === 'semantic' && !editSemanticQuery.trim()) {
      alert('Please enter a search query');
      return;
    }

    if (editSearchType === 'criteria' && hasRangeErrors(findRangeErrors(editCriteria))) {
      return;
    }

    try {
      const rawCriteria = editSearchType === 'semantic'
        ? { query: editSemanticQuery }
        : editCriteria;
      const criteria = buildCleanCriteria(rawCriteria, editSearchType);

      await updateSearch(editingId, {
        name: editName,
        criteria,
        notify_on_new_listing: editNotifications.notifyNewListing,
        notify_on_price_drop: editNotifications.notifyPriceDrop,
        notify_frequency: editNotifications.frequency,
      });

      handleCancelEdit();
    } catch (err) {
      console.error('Failed to update search:', err);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this saved search?')) {
      try {
        await deleteSearch(id);
      } catch (err) {
        console.error('Failed to delete search:', err);
      }
    }
  };

  const handleCheckListings = async (id) => {
    try {
      const result = await checkNewListings(id);
      alert(`Found ${result.new_notifications} new listings!`);
    } catch (err) {
      console.error('Failed to check for new listings:', err);
    }
  };

  const handleUseSearch = (search) => {
    if (onSelectSearch) {
      onSelectSearch(search);
    }
  };

  if (isLoading) {
    return (
      <div className="saved-searches-container">
        <div className="ss-skeleton" aria-busy="true" aria-label="Loading saved searches">
          <div className="ss-skeleton__row" />
          <div className="ss-skeleton__row" />
          <div className="ss-skeleton__row" />
        </div>
      </div>
    );
  }

  return (
    <div className="saved-searches-container">
      {/* ──── Hero Header ──── */}
      <div className="saved-searches-header">
        <div className="saved-searches-header__copy">
          <h2 className="saved-searches-title">
            <span className="saved-searches-title__eyebrow">Watchlist</span>
            Saved Searches
          </h2>
          <p className="saved-searches-subtitle">
            {searches.length === 0
              ? 'Get notified the moment a matching listing hits the market.'
              : `${searches.length} active ${searches.length === 1 ? 'search' : 'searches'} watching the market for you.`}
          </p>
        </div>
        <button
          onClick={() => setIsCreating(true)}
          className="create-button"
        >
          <span aria-hidden="true">＋</span> Add Search
        </button>
      </div>

      {error && (
        <div className="error-message" role="alert">
          <p>{error}</p>
        </div>
      )}

      {/* ──── Card Grid ──── */}
      {searches.length > 0 ? (
        <div className="searches-grid">
          {searches.map((search) => {
            const isSemantic = search.criteria?.search_type === 'semantic';
            return (
              <article
                key={search.id}
                className={`search-card ${isSemantic ? 'search-card--semantic' : 'search-card--criteria'}`}
              >
                <div
                  className={`search-card__accent ${isSemantic ? 'search-card__accent--semantic' : ''}`}
                  aria-hidden="true"
                />

                <header className="search-card__header">
                  <div className="search-card__title-block">
                    <h3 className="search-name" title={search.name}>{search.name}</h3>
                    {search.created_at && (
                      <p className="search-meta">
                        Saved {new Date(search.created_at).toLocaleDateString(undefined, {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </p>
                    )}
                  </div>
                </header>

                <CriteriaTags criteria={search.criteria} />

                <div className="notification-settings" aria-label="Notification preferences">
                  {search.notify_on_new_listing && (
                    <span className="setting" title="Notify on new listings">🔔 New Listings</span>
                  )}
                  {search.notify_on_price_drop && (
                    <span className="setting" title="Notify on price drops">💰 Price Drops</span>
                  )}
                  <span className="frequency" title="Notification frequency">
                    {search.notify_frequency || 'immediate'}
                  </span>
                </div>

                <footer className="search-actions">
                  <button
                    onClick={() => handleUseSearch(search)}
                    className="use-button"
                    title="Run this search now"
                  >
                    <span aria-hidden="true">▶</span> Use Search
                  </button>
                  <button
                    onClick={() => handleCheckListings(search.id)}
                    className="check-button"
                    title="Check for new listings"
                  >
                    Check Listings
                  </button>
                  <button
                    onClick={() => handleStartEdit(search)}
                    className="edit-button"
                    title="Edit search"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(search.id)}
                    className="delete-button"
                    title="Delete search"
                  >
                    Delete
                  </button>
                </footer>
              </article>
            );
          })}
        </div>
      ) : (
        <div className="empty-state">
          <div className="empty-state__art" aria-hidden="true">
            <div className="empty-state__art-bubble empty-state__art-bubble--a" />
            <div className="empty-state__art-bubble empty-state__art-bubble--b" />
            <div className="empty-state__art-icon">🔎</div>
          </div>
          <h3 className="empty-state__title">No saved searches yet</h3>
          <p className="empty-state__copy">
            Save a search and we'll ping you the moment a matching home hits the market.
          </p>
          <button
            type="button"
            onClick={() => setIsCreating(true)}
            className="create-button create-button--cta"
          >
            <span aria-hidden="true">＋</span> Create your first search
          </button>
        </div>
      )}

      {/* ──── Create Modal ──── */}
      {isCreating && (
        <SearchFormModal title="New saved search" onClose={resetCreateForm}>
          <SearchForm
            name={createName}
            setName={setCreateName}
            searchType={createSearchType}
            setSearchType={setCreateSearchType}
            criteria={createCriteria}
            onCriteriaChange={handleCreateCriteriaChange}
            idPrefix="create"
            semanticQuery={createSemanticQuery}
            setSemanticQuery={setCreateSemanticQuery}
            notifications={createNotifications}
            setNotifications={setCreateNotifications}
            onSubmit={handleCreateSearch}
            onCancel={resetCreateForm}
            submitLabel="Save Search"
          />
        </SearchFormModal>
      )}

      {/* ──── Edit Modal ──── */}
      {editingId && (
        <SearchFormModal title="Edit saved search" onClose={handleCancelEdit}>
          <SearchForm
            name={editName}
            setName={setEditName}
            searchType={editSearchType}
            setSearchType={setEditSearchType}
            criteria={editCriteria}
            onCriteriaChange={handleEditCriteriaChange}
            idPrefix="edit"
            semanticQuery={editSemanticQuery}
            setSemanticQuery={setEditSemanticQuery}
            notifications={editNotifications}
            setNotifications={setEditNotifications}
            onSubmit={handleSaveEdit}
            onCancel={handleCancelEdit}
            submitLabel="Save Changes"
          />
        </SearchFormModal>
      )}
    </div>
  );
}
